import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable, type Column } from '@/components/shared/DataTable';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useCurrency } from '@/hooks/useCurrency';

import { useUsageByGroupQuery, useUsageMonitoringFilters } from '../hooks/useUsageMonitoring';
import type { UsageByGroupRow, UsageGroupBy, UsageMetric } from '../types';
import { formatCount, formatRatioPct, formatSignedRatioPct } from '../utils/format';

function metricSortValue(metric: UsageMetric | undefined): number {
  return metric?.ratio ?? Number.NEGATIVE_INFINITY;
}

interface Props {
  onGroupClick?: (row: UsageByGroupRow, groupBy: UsageGroupBy) => void;
}

export function UsageBreakdownSection({ onGroupClick }: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'es';
  const { formatRounded } = useCurrency();
  const { isActionEnabled } = useFeatureVisibility();
  const allowExport = isActionEnabled('usage_monitoring.export_excel', false);
  const { queryParams } = useUsageMonitoringFilters();
  const [groupBy, setGroupBy] = useState<UsageGroupBy>('user');

  const byGroupQuery = useUsageByGroupQuery({ ...queryParams, group_by: groupBy });

  const columns = useMemo<Column[]>(
    () => [
      {
        header: t('adoption.breakdown.columns.group'),
        accessor: 'group_label',
        sortable: true,
        cell: (_v, row: UsageByGroupRow) => row.group_label || String(row.group_id),
        exportValue: (_v, row: UsageByGroupRow) => row.group_label || String(row.group_id),
      },
      {
        header: t('adoption.breakdown.columns.supplierDays'),
        accessor: 'supplier_days',
        sortable: true,
        numeric: true,
        align: 'right',
        exportType: 'integer',
      },
      {
        header: t('adoption.breakdown.columns.adoption'),
        accessor: 'adoption',
        sortable: true,
        numeric: true,
        align: 'right',
        sortValue: (_v, row: UsageByGroupRow) => metricSortValue(row.adoption),
        cell: (_v, row: UsageByGroupRow) => formatRatioPct(row.adoption.ratio, locale),
        exportValue: (_v, row: UsageByGroupRow) =>
          row.adoption.ratio == null ? '' : Math.round(row.adoption.ratio * 1000) / 10,
        exportType: 'number',
      },
      {
        header: t('adoption.breakdown.columns.lines'),
        accessor: 'lines',
        sortable: true,
        numeric: true,
        align: 'right',
        exportType: 'integer',
      },
      {
        header: t('adoption.breakdown.columns.ordersComplete'),
        accessor: 'orders_complete',
        sortable: true,
        numeric: true,
        align: 'right',
        sortValue: (_v, row: UsageByGroupRow) => metricSortValue(row.orders_complete),
        cell: (_v, row: UsageByGroupRow) => formatRatioPct(row.orders_complete.ratio, locale),
        exportValue: (_v, row: UsageByGroupRow) =>
          row.orders_complete.ratio == null
            ? ''
            : Math.round(row.orders_complete.ratio * 1000) / 10,
        exportType: 'number',
      },
      {
        header: t('adoption.breakdown.columns.adherence'),
        accessor: 'adherence',
        sortable: true,
        numeric: true,
        align: 'right',
        sortValue: (_v, row: UsageByGroupRow) => metricSortValue(row.adherence),
        cell: (_v, row: UsageByGroupRow) => formatRatioPct(row.adherence.ratio, locale),
        exportValue: (_v, row: UsageByGroupRow) =>
          row.adherence.ratio == null ? '' : Math.round(row.adherence.ratio * 1000) / 10,
        exportType: 'number',
      },
      {
        header: t('adoption.breakdown.columns.userBias'),
        accessor: 'user_bias',
        sortable: true,
        numeric: true,
        align: 'right',
        sortValue: (_v, row: UsageByGroupRow) => row.user_bias.ratio ?? Number.NEGATIVE_INFINITY,
        cell: (_v, row: UsageByGroupRow) => formatSignedRatioPct(row.user_bias.ratio, locale),
        exportValue: (_v, row: UsageByGroupRow) =>
          row.user_bias.ratio == null ? '' : Math.round(row.user_bias.ratio * 1000) / 10,
        exportType: 'number',
      },
      {
        header: t('adoption.breakdown.columns.direction'),
        accessor: 'user_direction',
        sortable: true,
        sortValue: (_v, row: UsageByGroupRow) =>
          row.user_direction.increased - row.user_direction.decreased,
        cell: (_v, row: UsageByGroupRow) =>
          t('adoption.kpis.userDirection', {
            equal: formatCount(row.user_direction.equal, locale),
            up: formatCount(row.user_direction.increased, locale),
            down: formatCount(row.user_direction.decreased, locale),
          }),
        exportValue: (_v, row: UsageByGroupRow) =>
          `${row.user_direction.equal}/${row.user_direction.increased}/${row.user_direction.decreased}`,
      },
      {
        header: t('adoption.breakdown.columns.addedOutside'),
        accessor: 'added_outside',
        sortable: true,
        numeric: true,
        align: 'right',
        sortValue: (_v, row: UsageByGroupRow) => metricSortValue(row.added_outside),
        cell: (_v, row: UsageByGroupRow) => formatRatioPct(row.added_outside.ratio, locale),
        exportValue: (_v, row: UsageByGroupRow) =>
          row.added_outside.ratio == null
            ? ''
            : Math.round(row.added_outside.ratio * 1000) / 10,
        exportType: 'number',
      },
      {
        header: t('adoption.breakdown.columns.totalBias'),
        accessor: 'total_bias',
        sortable: true,
        numeric: true,
        align: 'right',
        sortValue: (_v, row: UsageByGroupRow) => row.total_bias.ratio ?? Number.NEGATIVE_INFINITY,
        cell: (_v, row: UsageByGroupRow) => formatSignedRatioPct(row.total_bias.ratio, locale),
        exportValue: (_v, row: UsageByGroupRow) =>
          row.total_bias.ratio == null ? '' : Math.round(row.total_bias.ratio * 1000) / 10,
        exportType: 'number',
      },
      {
        header: t('adoption.breakdown.columns.totalBiasAmount'),
        accessor: 'total_bias_amount',
        sortable: true,
        numeric: true,
        align: 'right',
        sortValue: (value: number | null) => value ?? Number.NEGATIVE_INFINITY,
        cell: (value: number | null) => (value == null ? '—' : formatRounded(value)),
        exportValue: (value: number | null) => (value == null ? '' : Math.round(value)),
        exportType: 'currency',
      },
    ],
    [t, locale, formatRounded],
  );

  const errorMessage = (byGroupQuery.error as { message?: string } | null)?.message;

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-base font-semibold text-gray-900">
          {t('adoption.breakdown.title')}
        </h2>
        <label className="ml-auto block text-sm text-right">
          <span className="mb-1 block font-medium text-gray-700">
            {t('adoption.breakdown.dimension')}
          </span>
          <select
            className="min-w-[12rem] rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as UsageGroupBy)}
          >
            <option value="user">{t('adoption.breakdown.dimensions.user')}</option>
            <option value="supplier">{t('adoption.breakdown.dimensions.supplier')}</option>
          </select>
        </label>
      </div>

      {errorMessage ? (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('adoption.loadError')}: {errorMessage}
        </div>
      ) : null}

      <DataTable
        columns={columns}
        data={byGroupQuery.data?.results ?? []}
        loading={byGroupQuery.isLoading || byGroupQuery.isFetching}
        pagination
        allowExport={allowExport}
        exportFilename={`adopcion-desglose-${groupBy}`}
        compact
        resizableColumns
        onRowClick={
          onGroupClick
            ? (row: UsageByGroupRow) => onGroupClick(row, groupBy)
            : undefined
        }
      />
    </section>
  );
}
